import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ruler, User } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { CalculatorJsonLd } from '../components/seo/SchemaMarkup';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';

const BF_SEO = {
  name: "Body Fat Percentage Calculator",
  description: "Estimate your body fat percentage using the U.S. Navy method. Just enter your measurements to get your body fat, lean mass, and fitness category.",
  url: "https://ninjathlete.com/tools/body-fat-calculator",
  steps: [
    { name: "Select your gender", text: "Choose Male or Female, as the formula differs for each." },
    { name: "Choose measurement unit", text: "Select Imperial (inches) or Metric (centimeters)." },
    { name: "Enter measurements", text: "Provide your height, waist, and neck circumference. Women also enter hip measurement." },
    { name: "Calculate body fat", text: "Click Calculate to see your estimated body fat percentage, lean mass, fat mass, and fitness category." },
  ],
  faqs: [
    { question: "How accurate is the Navy body fat method?", answer: "The U.S. Navy method is accurate within 1-3% for most people. It's the most reliable tape-measure method available and is used by the U.S. military for fitness assessments." },
    { question: "What is a healthy body fat percentage?", answer: "For men: 10-20% is considered fit, 6-13% is athletic. For women: 18-28% is fit, 14-20% is athletic. Essential fat is 2-5% for men and 10-13% for women." },
    { question: "Where should I measure my waist?", answer: "Measure at the narrowest point of your waist, typically at the navel level. Keep the tape snug but not compressing the skin. Measure in the morning before eating." },
  ],
};

export default function BodyFatCalculatorPage() {
  const [gender, setGender] = useState('male');
  const [unit, setUnit] = useState('imperial');
  const [waist, setWaist] = useState('');
  const [neck, setNeck] = useState('');
  const [height, setHeight] = useState('');
  const [hip, setHip] = useState('');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const calculate = () => {
    if (!waist || !neck || !height || (gender === 'female' && !hip)) return;
    let wCm, nCm, hCm, hipCm;
    if (unit === 'imperial') {
      wCm = parseFloat(waist) * 2.54;
      nCm = parseFloat(neck) * 2.54;
      hCm = parseFloat(height) * 2.54;
      hipCm = gender === 'female' ? parseFloat(hip) * 2.54 : 0;
    } else {
      wCm = parseFloat(waist); nCm = parseFloat(neck); hCm = parseFloat(height);
      hipCm = gender === 'female' ? parseFloat(hip) : 0;
    }
    // US Navy Method
    let bf;
    if (gender === 'male') {
      bf = 495 / (1.0324 - 0.19077 * Math.log10(wCm - nCm) + 0.15456 * Math.log10(hCm)) - 450;
    } else {
      bf = 495 / (1.29579 - 0.35004 * Math.log10(wCm + hipCm - nCm) + 0.22100 * Math.log10(hCm)) - 450;
    }
    bf = Math.max(2, Math.min(60, bf));
    // Categories
    let category, color;
    if (gender === 'male') {
      if (bf < 6) { category = 'Essential Fat'; color = '#E63946'; }
      else if (bf < 14) { category = 'Athletic'; color = '#22c55e'; }
      else if (bf < 18) { category = 'Fitness'; color = '#00F0FF'; }
      else if (bf < 25) { category = 'Average'; color = '#FFD100'; }
      else { category = 'Above Average'; color = '#ff6b35'; }
    } else {
      if (bf < 14) { category = 'Essential Fat'; color = '#E63946'; }
      else if (bf < 21) { category = 'Athletic'; color = '#22c55e'; }
      else if (bf < 25) { category = 'Fitness'; color = '#00F0FF'; }
      else if (bf < 32) { category = 'Average'; color = '#FFD100'; }
      else { category = 'Above Average'; color = '#ff6b35'; }
    }
    setResult({ bf: bf.toFixed(1), category, color });
    setSaved(false);
  };

  const InputRow = ({ label, value, onChange, placeholder, testId }) => (
    <div data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <div className="relative">
        <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E63946]" />
        <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full pl-10 pr-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors" />
      </div>
    </div>
  );

  const categories = gender === 'male'
    ? [{ label: 'Essential', range: '2-5%' }, { label: 'Athletic', range: '6-13%' }, { label: 'Fitness', range: '14-17%' }, { label: 'Average', range: '18-24%' }, { label: 'Above Avg', range: '25%+' }]
    : [{ label: 'Essential', range: '10-13%' }, { label: 'Athletic', range: '14-20%' }, { label: 'Fitness', range: '21-24%' }, { label: 'Average', range: '25-31%' }, { label: 'Above Avg', range: '32%+' }];

  return (
    <ToolSEOWrapper toolKey="body-fat-calculator">
    <main className="pt-20 min-h-screen" data-testid="body-fat-calculator-page">
      <CalculatorJsonLd {...BF_SEO} calculatorName={BF_SEO.name} />
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="bf-calc-title">Body Fat Calculator</h1>
            <p className="text-zinc-400">Estimate your body fat percentage using the US Navy method</p>
          </div>
          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            <div className="mb-4">
              <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Gender</label>
              <div className="flex gap-2" data-testid="gender-toggle">
                {['male', 'female'].map(g => (
                  <button key={g} onClick={() => setGender(g)} className={`flex-1 py-3 text-sm font-heading uppercase tracking-wider transition-colors ${gender === g ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white'}`}>{g}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mb-4">
              {['imperial', 'metric'].map(u => (
                <button key={u} onClick={() => setUnit(u)} className={`flex-1 py-2 text-xs font-heading uppercase tracking-wider transition-colors ${unit === u ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'}`}>
                  {u === 'imperial' ? 'Inches' : 'Centimeters'}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <InputRow label={`Height (${unit === 'imperial' ? 'inches' : 'cm'})`} value={height} onChange={setHeight} placeholder="Height" testId="bf-height" />
              <InputRow label={`Neck Circumference (${unit === 'imperial' ? 'in' : 'cm'})`} value={neck} onChange={setNeck} placeholder="Neck" testId="bf-neck" />
              <InputRow label={`Waist at Navel (${unit === 'imperial' ? 'in' : 'cm'})`} value={waist} onChange={setWaist} placeholder="Waist" testId="bf-waist" />
              {gender === 'female' && (
                <InputRow label={`Hip Circumference (${unit === 'imperial' ? 'in' : 'cm'})`} value={hip} onChange={setHip} placeholder="Hips" testId="bf-hip" />
              )}
            </div>
            <button onClick={calculate} disabled={!waist || !neck || !height || (gender === 'female' && !hip)}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-bf-btn">
              Estimate Body Fat
            </button>
          </div>

          {result && (
            <div className="mt-8 space-y-6 animate-entrance" data-testid="bf-results">
              <div className="bg-zinc-900/80 border p-6 text-center" style={{ borderColor: `${result.color}50` }}>
                <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Estimated Body Fat</p>
                <p className="text-6xl font-heading text-white" data-testid="bf-value">{result.bf}<span className="text-2xl text-[#E63946]">%</span></p>
                <p className="text-lg font-heading mt-2" style={{ color: result.color }}>{result.category}</p>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 p-6">
                <h3 className="text-white text-lg font-heading mb-4">Body Fat Categories ({gender === 'male' ? 'Men' : 'Women'})</h3>
                <div className="grid grid-cols-5 gap-2">
                  {categories.map((c, i) => (
                    <div key={c.label} className={`p-3 text-center border ${c.label === result.category ? 'border-[#E63946] bg-[#E63946]/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                      <p className="text-white text-xs font-semibold">{c.label}</p>
                      <p className="text-zinc-500 text-[10px]">{c.range}</p>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-zinc-600 text-xs text-center">US Navy body fat formula. For a more accurate reading, consider DEXA scanning or hydrostatic weighing.</p>
              <SaveResultButton
                saved={saved}
                onSave={() => {
                  saveResult('bodyFat', {
                    value: parseFloat(result.bf),
                    label: `${result.bf}% body fat (${result.category})`,
                    category: result.category, gender,
                  });
                  setSaved(true);
                }}
              />
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
